import api from './api';
import {
  Invoice, CreateInvoiceRequest, UpdateInvoiceRequest,
  InvoiceStatus, TransitionInvoiceStatusRequest,
  InvoiceAuditEntry, CancelInvoiceRequest, SendInvoiceRequest, RevenueSummary,
} from '../types';

export const invoiceService = {
  getAll: async (params?: { clientId?: number; projectId?: number; status?: InvoiceStatus; includeInactive?: boolean; businessArea?: string }): Promise<Invoice[]> => {
    const response = await api.get('/invoices', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Invoice> => {
    const response = await api.get(`/invoices/${id}`);
    return response.data;
  },

  create: async (data: CreateInvoiceRequest): Promise<Invoice> => {
    const response = await api.post('/invoices', data);
    return response.data;
  },

  update: async (id: number, data: UpdateInvoiceRequest): Promise<Invoice> => {
    const response = await api.put(`/invoices/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/invoices/${id}`);
  },

  /**
   * Ordnet die Rechnung einem Vorhaben zu (leer = Zuordnung aufheben).
   *
   * Als einzige Angabe auch bei einer festgeschriebenen Rechnung erlaubt: der Bereich steht
   * nicht auf dem Dokument. Ohne diesen Weg ließe sich keine bereits versendete Rechnung
   * zuordnen — und genau die gibt es schon.
   */
  setBusinessArea: async (id: number, businessArea: string | null): Promise<Invoice> => {
    const response = await api.patch(`/invoices/${id}/business-area`, { businessArea });
    return response.data;
  },

  /** Umsatzzahlen eines Jahres; ohne Angabe das jüngste Jahr mit Rechnungen. */
  revenue: async (year?: number): Promise<RevenueSummary> => {
    const response = await api.get('/invoices/revenue', { params: year ? { year } : undefined });
    return response.data;
  },

  nextNumber: async (): Promise<string> => {
    const response = await api.get('/invoices/next-number');
    return response.data.number;
  },

  transitionStatus: async (id: number, status: InvoiceStatus): Promise<Invoice> => {
    const body: TransitionInvoiceStatusRequest = { status };
    const response = await api.post(`/invoices/${id}/transition`, body);
    return response.data;
  },

  /** Verschickt die Rechnung per E-Mail; ein Entwurf wird damit zugleich festgeschrieben. */
  send: async (id: number, data: SendInvoiceRequest): Promise<Invoice> => {
    const response = await api.post(`/invoices/${id}/send`, data);
    return response.data;
  },

  /** Storniert eine festgeschriebene Rechnung; liefert die neue Stornorechnung zurück. */
  cancel: async (id: number, reason: string): Promise<Invoice> => {
    const body: CancelInvoiceRequest = { reason };
    const response = await api.post(`/invoices/${id}/cancel`, body);
    return response.data;
  },

  auditLog: async (id: number): Promise<InvoiceAuditEntry[]> => {
    const response = await api.get(`/invoices/${id}/audit-log`);
    return response.data;
  },

  renderHtml: async (id: number): Promise<string> => {
    const response = await api.get(`/invoices/${id}/render`, { responseType: 'text' });
    return response.data;
  },

  /** Lädt das gerenderte Rechnungs-PDF als Blob (mit JWT-Header über axios). */
  renderPdf: async (id: number): Promise<Blob> => {
    const response = await api.get(`/invoices/${id}/render/pdf`, { responseType: 'blob' });
    return response.data;
  },
};

export default invoiceService;
